<template>
  <div class="shcm">

    <!-- ─── Hero ─────────────────────────────────────────────────────────── -->
    <header class="shcm__hero">
      <div class="shcm__hero-bg"></div>
      <div class="shcm__hero-glow"></div>

      <div class="shcm__hero-inner">
        <div class="shcm__hero-badge">
          <span class="shcm__hero-badge-dot">
            <span class="shcm__hero-badge-ping"></span>
          </span>
          {{ $t('home.SignalLabel') }}
        </div>

        <h1 class="shcm__hero-title">{{ $t('home.SignalTitle') }}</h1>
        <p class="shcm__hero-desc">{{ $t('home.SignalSubtitle') }}</p>

        <div class="shcm__hero-actions">
          <button class="shcm__btn shcm__btn--primary" @click="goToContact">
            {{ $t('home.startNow') }}
          </button>
          <a
            href="https://t.me/songhancapital"
            target="_blank"
            rel="noopener noreferrer"
            class="shcm__btn shcm__btn--secondary"
          >
            {{ $t('home.ctaFinalBtnSecondary') }}
          </a>
        </div>
      </div>

      <div class="shcm__hero-wave">
        <svg viewBox="0 0 1440 80" fill="none" preserveAspectRatio="none">
          <path d="M0 80L60 65C120 50 240 20 360 10C480 0 600 0 720 7.5C840 15 960 30 1080 37.5C1200 45 1320 45 1380 45L1440 45V80H0Z" fill="#f2f0ea"/>
        </svg>
      </div>
    </header>

    <!-- ─── Steps + Trade Log ─────────────────────────────────────────────── -->
    <section class="shcm__section shcm__section--light">
      <div class="shcm__shell">

        <div class="shcm__grid">

          <!-- Left: 4 steps -->
          <div class="shcm__steps">
            <div v-for="(step, i) in machineSteps" :key="i" class="shcm__step">
              <div class="shcm__step-number">{{ String(i + 1).padStart(2, '0') }}</div>
              <div class="shcm__step-content">
                <h3 class="shcm__step-title">{{ $t(step.titleKey) }}</h3>
                <p class="shcm__step-desc">{{ $t(step.descKey) }}</p>
                <div class="shcm__step-tags">
                  <span v-for="tag in step.tags" :key="tag" class="shcm__tag">{{ tag }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Right: Trade log panel -->
          <div class="shcm__panel">

            <!-- Header -->
            <div class="shcm__panel-header">
              <div class="shcm__panel-header-left">
                <span class="shcm__panel-title">{{ $t('home.autoTradeLog') }}</span>
                <span class="shcm__panel-sep">·</span>
                <span class="shcm__panel-today">{{ $t('home.oneDay') }}</span>
              </div>
              <div class="shcm__panel-header-right">
                <span class="shcm__panel-live-dot"></span>
                <span class="shcm__panel-live-text">Đang chạy</span>
              </div>
            </div>

            <!-- Sparkline -->
            <div class="shcm__sparkline">
              <svg :viewBox="`0 0 ${tradeChart.sparkW} 56`" preserveAspectRatio="none" class="shcm__sparkline-svg">
                <defs>
                  <linearGradient id="shcmSparkGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#2f8c56" stop-opacity="0.15"/>
                    <stop offset="100%" stop-color="#2f8c56" stop-opacity="0"/>
                  </linearGradient>
                </defs>
                <line x1="0" :y1="tradeChart.sparkZeroY" :x2="tradeChart.sparkW" :y2="tradeChart.sparkZeroY"
                  stroke="#e4ddd0" stroke-width="1"/>
                <path :d="tradeChart.sparkFill" fill="url(#shcmSparkGrad)"/>
                <polyline :points="tradeChart.sparkLine" fill="none" stroke="#2f8c56" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <div class="shcm__sparkline-labels">
                <span v-for="h in tradeChart.timeGrid" :key="h.label" v-show="h.isHour"
                  :style="{ left: h.pct + '%' }">{{ h.label }}</span>
              </div>
            </div>

            <!-- Column headers -->
            <div class="shcm__thead">
              <span>{{ $t('home.oneDay').replace('Hôm', 'Giờ') || 'Giờ' }}</span>
              <span>Pair</span>
              <span>{{ $t('home.entryPrice') }}</span>
              <span>P&amp;L</span>
            </div>

            <!-- Trade rows -->
            <div class="shcm__timeline">
              <div
                v-for="(trade, idx) in tradeChart.sorted"
                :key="idx"
                :class="['shcm__row', trade.profit > 0 ? 'shcm__row--win' : 'shcm__row--loss']"
              >
                <span class="shcm__row-time">{{ trade.time }}</span>
                <div class="shcm__row-pair-wrap">
                  <span class="shcm__row-pair">{{ trade.pair }}</span>
                  <span :class="['shcm__row-badge', trade.type === 'BUY' ? 'shcm__row-badge--buy' : 'shcm__row-badge--sell']">
                    {{ trade.type }}
                  </span>
                </div>
                <div class="shcm__row-meta">
                  <span class="shcm__row-price">{{ trade.price }}</span>
                  <span class="shcm__row-lot">{{ trade.lot }} lot</span>
                </div>
                <div class="shcm__row-pnl">
                  <div class="shcm__row-bar-wrap">
                    <div class="shcm__row-bar"
                      :style="{
                        width: Math.abs(trade.profit / tradeChart.maxAbsProfit * 100) + '%',
                        background: trade.profit > 0 ? 'linear-gradient(90deg,#2f8c56,#3daa6a)' : 'linear-gradient(90deg,#c0504d,#e06460)'
                      }"
                    ></div>
                  </div>
                  <span :class="['shcm__row-profit', trade.profit > 0 ? 'shcm__row-profit--win' : 'shcm__row-profit--loss']">
                    {{ trade.profit > 0 ? '+' : '' }}{{ trade.profit.toFixed(2) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="shcm__panel-footer">
              <span>{{ tradeChart.total }} lệnh</span>
              <span class="shcm__panel-footer-sep">·</span>
              <span class="shcm__panel-footer-profit">+{{ tradeChart.totalProfit }}</span>
              <span class="shcm__panel-footer-sep">·</span>
              <span>Win {{ tradeChart.winRate }}%</span>
            </div>

          </div>
        </div>

      </div>
    </section>

    <!-- ─── CTA ───────────────────────────────────────────────────────────── -->
    <section class="shcm__cta">
      <div class="shcm__shell">
        <p class="shcm__cta-text">{{ $t('home.ctaFinalDesc') }}</p>
        <div class="shcm__cta-actions">
          <button class="shcm__btn shcm__btn--primary" @click="goToContact">
            {{ $t('home.ctaFinalBtnPrimary') }}
          </button>
          <a
            href="https://t.me/songhancapital"
            target="_blank"
            rel="noopener noreferrer"
            class="shcm__btn shcm__btn--outline"
          >
            {{ $t('home.ctaFinalBtnSecondary') }}
          </a>
        </div>
      </div>
    </section>

  </div>
</template>

<script src="./shc-machine.js"></script>
<style src="./shc-machine.scss" lang="scss"></style>
